import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table } from 'antd';
import { Button } from '../../../../components/button';
import { DownloadOutlined } from '@ant-design/icons';
import { Grant } from '../../../../stores/promotion-grant';
import { PaginatedList } from '../../../../types';
import { Pagination } from '../../../../components/pagination';
import { TEN_PER_PAGE } from '../../../../utils/const';
import { formatAppTargets } from '../../../../utils/app-target';
import style from './style.module.scss';
import { ToggleableSection } from '../../../../components/form-fields/toggleable-section';
import { Tag } from '../../../../components/tag';
import star from '../../../../icons/Icon_Coupons_Gagnants_1_1X.svg';

type WinnableSectionProps = {
    isPrivate: boolean | null | undefined;
    setFieldValue: (
        field: string,
        value: unknown,
        shouldValidate?: boolean
    ) => void;
    fetchGrants: (page: number, perPage: number) => Promise<void>;
    grantsData: PaginatedList<Grant> | null;
    isFetching: boolean;
    downloadWinners: () => void;
};

const columns = [
    {
        title: 'Client ID',
        dataIndex: 'clientId',
        key: 'clientId',
    },
    {
        title: 'Email',
        dataIndex: ['client', 'email'],
        key: 'email',
    },
    {
        title: 'Name',
        key: 'name',
        render: (_: unknown, record: Grant) =>
            `${record.client.firstName} ${record.client.lastName}`,
    },
    {
        title: 'App',
        key: 'appTargets',
        render: (_: unknown, record: Grant) =>
            formatAppTargets(record.client.appTargets),
    },
    {
        title: 'Language',
        key: 'language',
        render: (_: unknown, record: Grant) => (
            <Tag variant={record.client.language === 'fr' ? 'blue' : 'magenta'}>
                {record.client.language}
            </Tag>
        ),
    },
    {
        title: 'Number of Wins',
        key: 'numberOfWins',
        render: (_: unknown, record: Grant) => (
            <div className={style.star_container}>
                <img className={style.star_img} src={star} alt="star icon" />
                <span className={style.normal}>{record.cnt}</span>
            </div>
        ),
    },
];

export const WinnableSection = ({
    isPrivate,
    setFieldValue,
    fetchGrants,
    grantsData,
    isFetching,
    downloadWinners,
}: WinnableSectionProps) => {
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(TEN_PER_PAGE);

    const fetchGrantsRef = useRef(fetchGrants);
    const prevGrantsDataRef = useRef(grantsData);

    useEffect(() => {
        fetchGrantsRef.current = fetchGrants;
    }, [fetchGrants]);

    useEffect(() => {
        prevGrantsDataRef.current = grantsData;
    }, [grantsData]);

    useEffect(() => {
        if (fetchGrantsRef.current) {
            const apiPage = page + 1;
            const grantsDataChanged = prevGrantsDataRef.current !== grantsData;

            if (
                !grantsData ||
                grantsData.page !== apiPage ||
                grantsData.perPage !== perPage ||
                grantsDataChanged
            ) {
                fetchGrantsRef.current(page, perPage);
            }
        }
    }, [page, perPage, grantsData]);

    const handleSwitchChange = useCallback(
        (checked: boolean) => {
            setFieldValue('isPrivate', checked);
        },
        [setFieldValue]
    );

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPerPage: number) => {
        setPerPage(newPerPage);
        setPage(0);
    }, []);

    const displayGrants = useMemo(() => grantsData?.results ?? [], [
        grantsData?.results,
    ]);

    return (
        <ToggleableSection
            title="Winnable"
            checked={isPrivate ?? false}
            onToggleChange={handleSwitchChange}
        >
            <div>
                <div className={style.winnable_buttons_row}>
                    <Button
                        variant="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadWinners}
                    >
                        Download List
                    </Button>
                </div>
                <div className={style.full_width_table}>
                    <Table<Grant>
                        rowKey="id"
                        columns={columns}
                        dataSource={displayGrants}
                        pagination={false}
                        loading={isFetching}
                    />
                </div>
                {grantsData && (
                    <div className={style.pagination_container}>
                        <Pagination
                            page={grantsData.page ? grantsData.page - 1 : 0}
                            pageSize={grantsData.perPage}
                            total={grantsData.total}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                )}
            </div>
        </ToggleableSection>
    );
};
